import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Card, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { ApiError } from '@/api/client';
import { useAuth } from './AuthContext';
import { NoDispoAccessError } from './errors';

type LoginMessageKey =
    | 'auth.login.invalidCredentials'
    | 'auth.login.locked'
    | 'auth.login.noAccess'
    | 'auth.login.failed'
    | 'common.networkError';

function messageKeyFor(error: unknown): LoginMessageKey {
    if (error instanceof NoDispoAccessError) {
        return 'auth.login.noAccess';
    }
    if (error instanceof ApiError) {
        const name =
            typeof error.body === 'object' && error.body !== null && 'name' in error.body ? error.body.name : null;
        if (error.status === 401 && name === 'TooManyFailedLogins') {
            return 'auth.login.locked';
        }
        if (error.status === 401) {
            return 'auth.login.invalidCredentials';
        }
        if (error.status === 0) {
            return 'common.networkError';
        }
    }
    return 'auth.login.failed';
}

export function LoginPage() {
    const { t } = useTranslation();
    const { user, login, logoutReason } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = (location.state as { from?: string } | null)?.from ?? '/';

    const [loginName, setLoginName] = useState('');
    const [password, setPassword] = useState('');
    const [messageKey, setMessageKey] = useState<LoginMessageKey | null>(null);
    const [busy, setBusy] = useState(false);

    async function onSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setBusy(true);
        setMessageKey(null);
        try {
            await login(loginName, password);
            navigate(from, { replace: true });
        } catch (error) {
            setMessageKey(messageKeyFor(error));
        } finally {
            setBusy(false);
        }
    }

    if (user) {
        return <Navigate to={from} replace />;
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'grey.100' }}>
            <Card component="form" onSubmit={onSubmit} sx={{ p: 4, width: 360 }}>
                <Stack spacing={2}>
                    <Typography variant="h5" component="h1">
                        {t('auth.login.title')}
                    </Typography>
                    {logoutReason === 'expired' && <Alert severity="info">{t('auth.login.sessionExpired')}</Alert>}
                    {messageKey && <Alert severity="error">{t(messageKey)}</Alert>}
                    <TextField
                        id="login"
                        label={t('auth.login.user')}
                        value={loginName}
                        onChange={(e) => setLoginName(e.target.value)}
                        autoComplete="username"
                        autoFocus
                        required
                    />
                    <TextField
                        id="password"
                        type="password"
                        label={t('auth.login.password')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                    />
                    <Button type="submit" variant="contained" disabled={busy}>
                        {t('auth.login.submit')}
                    </Button>
                </Stack>
            </Card>
        </Box>
    );
}
